/**
 * PO Ingest Page
 *
 * The first step in the HITL workflow.
 * Allows users to upload purchase order documents.
 *
 * Flow:
 * 1. User uploads one or more documents (PDF, image, EDI, Excel, CSV, text)
 * 2. Documents are saved to S3 and added to the queue with 'pending' status
 * 3. User is redirected to Document Queue to manage and process documents
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentUploader from '../components/documents/DocumentUploader';
import Button from '../components/common/Button';
import * as extractionApi from '../api/extractionApi';

/**
 * POIngest Component
 */
function POIngest() {
  const navigate = useNavigate();

  // State
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  /**
   * Handle file selection from uploader
   * @param {File[]} files - Selected files
   */
  const handleFileSelect = (files) => {
    setSelectedFiles(Array.isArray(files) ? files : [files].filter(Boolean));
    setError(null);
  };

  /**
   * Handle upload and proceed to review
   */
  const handleProceed = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Upload all files and collect their info
      const uploadedFiles = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const response = await extractionApi.uploadDocument(file);
        uploadedFiles.push({
          queueId: response.data.queueId,
          objectKey: response.data.objectKey,
          mimeType: response.data.mimeType,
          signedUrl: response.data.signedUrl,
          originalName: file.name,
          status: response.data.status
        });
        setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
      }

      // Navigate to the document queue page
      // Documents are now in the queue with 'pending' status
      navigate('/document-queue');
    } catch (err) {
      setError(err.message || 'Failed to upload documents. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Upload Purchase Orders
        </h1>
        <p className="mt-2 text-gray-600">
          Upload one or more purchase order documents and our AI will extract the data for you to review.
        </p>
      </div>

      {/* Upload Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Error Alert */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="ml-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Document Uploader */}
        <DocumentUploader
          onFileSelect={handleFileSelect}
          disabled={isUploading}
          multiple
        />

        {/* Upload Progress */}
        {isUploading && uploadProgress > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Uploading files...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Proceed Button */}
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleProceed}
            disabled={selectedFiles.length === 0}
            loading={isUploading}
            size="lg"
          >
            {isUploading
              ? 'Uploading...'
              : `Upload to Queue${selectedFiles.length > 1 ? ` (${selectedFiles.length} files)` : ''}`
            }
          </Button>
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h3 className="font-medium text-gray-900">What happens next?</h3>
        <ul className="mt-3 space-y-2 text-sm text-gray-600">
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Documents are securely uploaded to the processing queue
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Select documents from the queue to start AI extraction
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Review extracted data side-by-side with the original document
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Approve to create purchase orders or reject invalid documents
          </li>
        </ul>
      </div>
    </div>
  );
}

export default POIngest;
